import { WrapItem, Wrap } from '@chakra-ui/react'
import _ from 'lodash'
import type { RefObject } from 'react'
import React, { useCallback, useMemo } from 'react'

import type {
  SearchQueryJson,
  FormValidationObject,
  CustomMetadataResponse,
  FieldIdentifier,
  BooleanOperator,
  RemoveFilterProps,
} from '../../interfaces'
import { getDefaultExpression } from '../../../utils/mediaLibrary'
import AddFilterMenu from './AddFilterMenu'
import FilterGroupNew from './Search/FilterGroup'
import SearchBoolean from './Search/SearchBoolean'

interface Props {
  page?: 'media-library' | 'media-collection'
  searchQueryJson: SearchQueryJson
  customMetadataFields: CustomMetadataResponse[]
  customMetadataFieldsMapping: { [fieldName: string]: CustomMetadataResponse }
  validationObject: FormValidationObject
  maxWidthRef: RefObject<any>
  setSearchQueryJson(SearchQueryJson: SearchQueryJson): void
  removeFilter(removeFilterProps: RemoveFilterProps): void
}

const AdvancedSearch = (props: Props): React.ReactElement => {
  const {
    searchQueryJson,
    setSearchQueryJson,
    validationObject,
    customMetadataFields,
    customMetadataFieldsMapping,
    removeFilter,
    maxWidthRef,
    page = 'media-library',
  } = props

  const onChangeBoolean = useCallback(
    (newBoolean: BooleanOperator) => {
      const newSearchQueryJson = _.cloneDeep(searchQueryJson)
      newSearchQueryJson.boolean = newBoolean
      setSearchQueryJson(newSearchQueryJson)
    },
    [searchQueryJson],
  )

  const onAddFilter = useCallback(
    (fieldIdentifier: FieldIdentifier) => {
      const newSearchQueryJson = _.cloneDeep(searchQueryJson)
      newSearchQueryJson.filterGroups.push({
        boolean: 'and',
        expressions: [getDefaultExpression(fieldIdentifier)],
      })
      setSearchQueryJson(newSearchQueryJson)
    },
    [searchQueryJson],
  )

  return (
    <Wrap spacing="0" align="center">
      {searchQueryJson.filterGroups.map((filterGroup, index) => (
        <React.Fragment key={index}>
          <FilterGroupNew
            page={page}
            key={index}
            {...{
              searchQueryJson,
              setSearchQueryJson,
              customMetadataFields,
              customMetadataFieldsMapping,
              validationObject,
              groupId: index,
              maxWidthRef,
              removeFilter,
            }}
          />
          {index !== searchQueryJson.filterGroups.length - 1 && (
            <WrapItem>
              <SearchBoolean
                scope="outer"
                boolean={searchQueryJson.boolean}
                onChangeBoolean={onChangeBoolean}
              />
            </WrapItem>
          )}
        </React.Fragment>
      ))}
      <AddFilterMenu
        page={page}
        scope="outer"
        customMetadataFields={customMetadataFields}
        onAddFilter={onAddFilter}
      />
    </Wrap>
  )
}

export default AdvancedSearch
